import { useEffect, useMemo, useState } from 'react';
import { tripTotal } from '@heivol-finance/calc';
import {
  travelApi,
  type TravelData,
  type TripRow,
  type TripCostRow,
  type TripStatus,
} from '../lib/data.js';
import { formatOre } from '../lib/format.js';
import {
  PageHeader,
  CollapsibleCard,
  StatCard,
  Button,
  DeleteButton,
  TextField,
  OreField,
  Select,
  EmptyState,
} from '../components/ui.js';
import { SectionBar, Tr, Th, Td } from '../components/table.js';

const STATUS_OPTS: { value: TripStatus; label: string }[] = [
  { value: 'idea', label: 'Idea' },
  { value: 'booked', label: 'Booked' },
  { value: 'paid', label: 'Paid' },
];
const statusLabel = (s: TripStatus) => STATUS_OPTS.find((o) => o.value === s)?.label ?? s;
// idea = coral/chat (not committed), booked = ai/violet (reserved), paid = success/green (spent).
const statusTone: Record<TripStatus, string> = {
  idea: 'bg-hv-chat-surface text-hv-chat-text',
  booked: 'bg-hv-ai-surface text-hv-ai-text',
  paid: 'bg-hv-success-surface text-hv-success-text',
};

// Common travel cost categories (free text is still allowed via the input).
const CATEGORY_OPTS: { value: string; label: string }[] = [
  { value: 'fly', label: 'Flights' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'transport', label: 'Transport' },
  { value: 'dagligt', label: 'Daily' },
  { value: 'aktiviteter', label: 'Activities' },
  { value: 'andet', label: 'Other' },
];

/** 'YYYY-MM-DD' → 'MM/YYYY' for the month display, guarding a malformed string. */
function monthLabel(month: string): string {
  const parts = month.split('-');
  const y = parts[0] ?? '2026';
  const m = parts[1] ?? '01';
  return `${m}/${y}`;
}

export function Travel(): JSX.Element {
  const [data, setData] = useState<TravelData | null>(null);
  const reload = () => travelApi.load().then(setData);
  useEffect(() => {
    void reload();
  }, []);

  const costsByTrip = useMemo(() => {
    const map = new Map<string, TripCostRow[]>();
    for (const cost of data?.costs ?? []) {
      const list = map.get(cost.trip_id) ?? [];
      list.push(cost);
      map.set(cost.trip_id, list);
    }
    return map;
  }, [data]);

  const totals = useMemo(() => {
    let all = 0;
    let posted = 0;
    for (const trip of data?.trips ?? []) {
      const total = tripTotal((costsByTrip.get(trip.id) ?? []).map((c) => c.amount_ore));
      all += total;
      if (trip.posted) posted += total;
    }
    return { all, posted };
  }, [data, costsByTrip]);

  if (!data) return <PageHeader title="Travel" subtitle="Loading…" />;

  return (
    <div className="max-w-5xl">
      <PageHeader
        title="Travel"
        subtitle={
          <>
            Planned trips with cost lines (flights, hotel, transport, daily, activities). A
            trip's total amount can be posted to Net Worth as a consumptive one-off in the trip's
            month — trips draw down your accumulated net worth.
          </>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Total trips" value={formatOre(totals.all)} tone="chat" hint="Sum of all trips" />
        <StatCard label="Posted to Net Worth" value={formatOre(totals.posted)} tone="ai" hint="Counts on the net-worth curve" />
        <StatCard label="Number of trips" value={String(data.trips.length)} />
      </div>

      <div className="mb-4 flex justify-end">
        <Button
          variant="secondary"
          onClick={() => {
            const now = new Date();
            const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
            travelApi.addTrip({ name: 'New trip', month, status: 'idea' }).then(reload);
          }}
        >
          + Trip
        </Button>
      </div>

      {data.trips.length === 0 ? (
        <EmptyState>
          No trips yet. Add a trip, give it a month and a status (idea / booked / paid), and record
          costs as lines.
        </EmptyState>
      ) : (
        <div className="space-y-6">
          {data.trips.map((trip) => (
            <TripCard key={trip.id} trip={trip} costs={costsByTrip.get(trip.id) ?? []} reload={reload} />
          ))}
        </div>
      )}

      <p className="mt-6 text-hv-xs text-hv-text-tertiary">
        A trip is <span className="text-hv-chat-text">consumptive</span> — post it to Net Worth to subtract its amount from
        your accumulated net worth in the trip's month. The posting can be updated when the amount changes, or removed again.
      </p>
    </div>
  );
}

function TripCard({
  trip,
  costs,
  reload,
}: {
  trip: TripRow;
  costs: TripCostRow[];
  reload: () => Promise<void>;
}): JSX.Element {
  const patch = (b: Partial<TripRow>) => travelApi.updateTrip(trip.id, b).then(reload);
  const total = useMemo(() => tripTotal(costs.map((c) => c.amount_ore)), [costs]);

  const postLabel = trip.posted ? 'Update in Net Worth' : 'Post to Net Worth';

  return (
    <CollapsibleCard
      summary={
        <div className="flex items-center gap-2">
          <span>{trip.name}</span>
          <span className={`rounded-hv-md px-2 py-0.5 text-hv-xs ${statusTone[trip.status]}`}>
            {statusLabel(trip.status)}
          </span>
          {trip.posted && (
            <span className="rounded-hv-md bg-hv-ai-surface px-2 py-0.5 text-hv-xs text-hv-ai-text">Posted</span>
          )}
          <span className="ml-auto pl-2 tabular-nums text-hv-text-secondary">{formatOre(total)}</span>
        </div>
      }
      actions={<DeleteButton onClick={() => travelApi.deleteTrip(trip.id).then(reload)} label="Delete trip" />}
    >
      {/* Parameters */}
      <div className="grid grid-cols-2 gap-4 border-b border-hv-border p-4 sm:grid-cols-3 lg:grid-cols-4">
        <Labeled label="Trip">
          <TextField value={trip.name} onCommit={(v) => patch({ name: v })} />
        </Labeled>
        <Labeled label="Month">
          <TextField value={trip.month} placeholder="YYYY-MM-DD" onCommit={(v) => patch({ month: v })} />
        </Labeled>
        <Labeled label="Status">
          <Select value={trip.status} options={STATUS_OPTS} onChange={(v) => patch({ status: v })} />
        </Labeled>
        <Labeled label="Total">
          <div className="px-2.5 py-1.5 text-hv-sm font-medium tabular-nums text-hv-text">{formatOre(total)}</div>
        </Labeled>
      </div>

      {/* Derived stats + post action */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-hv-border p-4">
        <div className="grid flex-1 grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label="Total" value={formatOre(total)} tone="chat" hint="Sum of costs" />
          <StatCard label="Month" value={monthLabel(trip.month)} hint={statusLabel(trip.status)} />
          <StatCard
            label="In Net Worth"
            value={trip.posted ? 'Yes' : 'No'}
            tone={trip.posted ? 'ai' : 'default'}
            hint={trip.posted ? 'Consumptive one-off' : 'Not posted'}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => travelApi.postToNetWorth(trip.id).then(reload)}>{postLabel}</Button>
          {trip.posted && (
            <Button variant="ghost" onClick={() => travelApi.unpostFromNetWorth(trip.id).then(reload)}>
              Remove from Net Worth
            </Button>
          )}
        </div>
      </div>

      {/* Cost lines */}
      <SectionBar
        title="Costs"
        onAdd={() => travelApi.addCost(trip.id, { category: 'fly', amount_ore: 0 }).then(reload)}
      />
      <div className="overflow-x-auto">
        <table className="w-full text-hv-sm">
          <thead>
            <Tr head>
              <Th>Category</Th>
              <Th right>Amount</Th>
              <Th />
            </Tr>
          </thead>
          <tbody>
            {costs.map((cost) => (
              <Tr key={cost.id}>
                <Td>
                  <Select
                    value={CATEGORY_OPTS.some((o) => o.value === cost.category) ? cost.category : 'andet'}
                    options={CATEGORY_OPTS}
                    onChange={(v) => travelApi.updateCost(trip.id, cost.id, { category: v }).then(reload)}
                    className="max-w-[12rem]"
                  />
                </Td>
                <Td right>
                  <OreField
                    value={cost.amount_ore}
                    onCommit={(v) => travelApi.updateCost(trip.id, cost.id, { amount_ore: v }).then(reload)}
                  />
                </Td>
                <Td>
                  <DeleteButton onClick={() => travelApi.deleteCost(trip.id, cost.id).then(reload)} />
                </Td>
              </Tr>
            ))}
            {costs.length === 0 && (
              <Tr>
                <Td colSpan={3}>
                  <span className="text-hv-text-tertiary">
                    No costs. Add lines (flights, hotel, transport, daily, activities) — each with a category
                    and an amount.
                  </span>
                </Td>
              </Tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="border-t border-hv-border px-4 py-2 text-hv-xs text-hv-text-tertiary">
        The trip's total amount is {formatOre(total)}.{' '}
        {trip.posted
          ? 'Posted to Net Worth — update if the amount changes, or remove it again.'
          : "Post to Net Worth to subtract the amount from your accumulated net worth in the trip's month."}
      </div>
    </CollapsibleCard>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-hv-xs uppercase tracking-wide text-hv-text-tertiary">{label}</span>
      {children}
    </label>
  );
}
